import pick from 'lodash/pick';
import omit from 'lodash/omit';
import queryString from 'query-string';
import { formatUrl } from 'url-lib';
import { pruneObject, getCanonicalSearchUrl } from '@eb/destination-utils';

import {
    START_DATE,
    END_DATE,
    EVENT_PAGE_PARAM,
    SEARCH_RELEVANT_QUERY_PARAMS,
    FOLLOWED_ORGS_FILTER_VALUE,
} from '../constants';

/*
 *
 * @param path {string} -- canonical path
 * @param eventSearch {object} -- state.search.eventSearch
 * @param previousLocationState {object} -- state.routing.locationBeforeTransitions
 * @param currentPage {number} -- the current page in paginated state
 * @param queryParamsToOmit {array} -- params to omit from outputted query object
 *
 * Func creates the appropriate URL object with approved and formatted query params. Assumes incoming
 * path is canonical already.
 *
 */
export const constructObjectForUrl = (
    path,
    eventSearch,
    previousLocationState,
    currentPage = 0,
    queryParamsToOmit = [],
) => {
    const existingQuery = omit(previousLocationState.query, queryParamsToOmit);

    const query = pruneObject({
        ...existingQuery,
        [EVENT_PAGE_PARAM]: currentPage + 1,
        [START_DATE]: eventSearch.dateRange.from,
        [END_DATE]: eventSearch.dateRange.to,
        bbox: eventSearch.bbox,
        cur: eventSearch.currencies,
        lang: eventSearch.languages,
        followed_orgs:
            eventSearch.special.indexOf(FOLLOWED_ORGS_FILTER_VALUE) > -1
                ? 1
                : '',
        organization: eventSearch.organizationsOr,
        hash_a: eventSearch.hashA,
    });

    return {
        ...previousLocationState,
        query,
        pathname: path,
    };
};

/*
@param path - String
@param queryParams - Object

Returns url formatted path with sanitized queryParams included
*/
export const getPathWithSanitizedQueryParams = (
    path,
    queryParams,
    whiteListedQueryParams = SEARCH_RELEVANT_QUERY_PARAMS,
) => {
    /* Query params we care about that will influence the search value */
    const sanitizedQuery = pick(queryParams, whiteListedQueryParams);

    return formatUrl(path, sanitizedQuery);
};

/*
@param page {number} - page param to set in query string
@param eventSearch {object} - takes current application state
@param locationSlug {string} - location slug like ca--san-francisco
@param historyLocation {object} - the history.location object from React-Router
Returns a formatted URL with appropriate query params and updated event page value
*/
export const constructPaginatedUrl = (
    page,
    eventSearch,
    locationSlug,
    historyLocation,
) => {
    const path = getCanonicalSearchUrl({ slug: locationSlug, ...eventSearch });

    // translate the react-router v4 prop to this older one
    const locationBeforeTransitions = {
        query: queryString.parse(historyLocation.search.replace('?', '')),
        pathname: historyLocation.pathname,
        hash: historyLocation.hash,
    };

    const { query: constructedQuery } = constructObjectForUrl(
        path,
        eventSearch,
        locationBeforeTransitions,
        page,
    );

    // maintain the url hash to help with testing new features
    const hash = 'hash' in historyLocation ? historyLocation.hash : '';

    return formatUrl(path, constructedQuery) + hash;
};
